import { formaPagoOfima } from '../assets/data/arrays'
import type {
  OfimaPedidoListItem,
  OfimaPedidosClientFilters,
  OfimaPedidosQuery,
  OfimaTipoDocumento
} from '../types/ofimaPedidos'

export const OFIMA_TIPO_DOCUMENTO_OPTIONS: Array<{ label: string; value: OfimaTipoDocumento }> = [
  { value: 'RE', label: 'RE · Remisión' },
  { value: 'FE', label: 'FE · Factura' },
  { value: 'FS', label: 'FS · Factura' },
  { value: 'FR', label: 'FR · Factura' },
  { value: 'NC', label: 'NC · Nota crédito' }
]

export const OFIMA_PEDIDOS_LIMIT_OPTIONS = [20, 50, 100, 150]

const OFIMA_TIPOS_DCTO = new Set<string>(OFIMA_TIPO_DOCUMENTO_OPTIONS.map((option) => option.value))

/**
 * RE, FE, FS, FR y NC en mayúsculas, separados por coma.
 * Vacío se omite. Un tipo desconocido no se envía.
 */
export function normalizeOfimaTipoDcto(value?: string | string[] | null): string | undefined {
  if (value == null) return undefined
  const tokens = (Array.isArray(value) ? value : value.split(','))
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean)
  if (tokens.length === 0) return undefined

  const unique: string[] = []
  for (const token of tokens) {
    if (!OFIMA_TIPOS_DCTO.has(token)) {
      throw new Error('Tipo de documento no válido')
    }
    if (!unique.includes(token)) unique.push(token)
  }
  return unique.join(',')
}

const OFIMA_ORDEN_MAX_LENGTH = 20

export function validateOfimaOrdenQuery(orden: string): string | null {
  const value = orden.trim()
  if (!value) return null
  if (value.length > OFIMA_ORDEN_MAX_LENGTH) {
    return 'El pedido no puede superar 20 caracteres'
  }
  return null
}

export function buildOfimaPedidosQuery(input: OfimaPedidosQuery = {}): {
  page: number
  limit: number
  orden?: string
  tipodcto?: string
} {
  const page = Number.isFinite(input.page) && (input.page as number) >= 1
    ? Math.trunc(input.page as number)
    : 1
  const rawLimit = Number.isFinite(input.limit) ? Math.trunc(input.limit as number) : 20
  const limit = Math.min(150, Math.max(1, rawLimit))
  const orden = input.orden?.trim() ?? ''
  const ordenError = validateOfimaOrdenQuery(orden)
  if (ordenError) {
    throw new Error(ordenError)
  }
  const tipodcto = normalizeOfimaTipoDcto(input.tipodcto)
  return {
    page,
    limit,
    ...(orden ? { orden } : {}),
    ...(tipodcto ? { tipodcto } : {})
  }
}

export function toOfimaDateKey(value: string | null | undefined): string {
  if (!value) return ''
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

export function formatOfimaDate(value: string | null | undefined): string {
  const key = toOfimaDateKey(value)
  if (!key) return 'N/A'
  const [year, month, day] = key.split('-')
  return `${day}/${month}/${year}`
}

export function labelFormaPagoOfima(value: string | null | undefined): string {
  const raw = (value ?? '').trim()
  if (!raw) return ''
  const found = (formaPagoOfima as Array<{ value: string; label: string }>).find(
    (item) => item.value.toLowerCase() === raw.toLowerCase()
  )
  return found?.label ?? raw
}

export function ofimaDisplayValue(value: string | null | undefined): string {
  const text = (value ?? '').trim()
  if (!text || text.toUpperCase() === 'N/A') return '—'
  return text
}

export function formatOfimaMoney(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return 'N/A'
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(amount)
}

function tiposFacturaDe(pedido: {
  tiposFactura?: string[] | string | null
}): string[] {
  const raw = pedido.tiposFactura as unknown
  const list = Array.isArray(raw)
    ? raw
    : typeof raw === 'string'
      ? raw.split(',')
      : []
  return list
    .map((tipo) => String(tipo).trim().toUpperCase())
    .filter(Boolean)
}

export function facturaTiposVisibles(pedido: {
  tiposFactura?: string[] | string | null
  cantidadFacturasFe?: number
  cantidadFacturasFs?: number
  cantidadFacturasFr?: number
}): string[] {
  const tipos = tiposFacturaDe(pedido)
  const add = (tipo: string, count?: number) => {
    if ((count ?? 0) > 0 && !tipos.includes(tipo)) tipos.push(tipo)
  }
  add('FE', pedido.cantidadFacturasFe)
  add('FS', pedido.cantidadFacturasFs)
  add('FR', pedido.cantidadFacturasFr)
  return tipos
}

export function pedidoTieneTipoDocumento(
  pedido: OfimaPedidoListItem,
  tipo: string
): boolean {
  const normalized = tipo.trim().toUpperCase()
  const tipos = tiposFacturaDe(pedido)

  switch (normalized) {
    case 'RE':
      return pedido.cantidadRemisiones > 0
    case 'FE':
      return pedido.cantidadFacturasFe > 0 || tipos.includes('FE')
    case 'FS':
      return pedido.cantidadFacturasFs > 0 || tipos.includes('FS')
    case 'FR':
      return pedido.cantidadFacturasFr > 0 || tipos.includes('FR')
    case 'NC':
      return pedido.cantidadNotasCredito > 0
    default:
      return tipos.includes(normalized)
  }
}

export function filterOfimaPedidos(
  pedidos: OfimaPedidoListItem[],
  filters: OfimaPedidosClientFilters
): OfimaPedidoListItem[] {
  const nit = filters.nit.trim().toLowerCase()

  return pedidos.filter((pedido) => {
    if (nit && !String(pedido.nit ?? '').toLowerCase().includes(nit)) {
      return false
    }

    if (filters.fechaDesde || filters.fechaHasta) {
      const fecha = toOfimaDateKey(pedido.fecha)
      if (!fecha) return false
      if (filters.fechaDesde && fecha < filters.fechaDesde) return false
      if (filters.fechaHasta && fecha > filters.fechaHasta) return false
    }

    if (filters.soloMultiplesRemisiones && !pedido.multiplesRemisiones) return false
    if (filters.soloMultiplesFacturas && !pedido.multiplesFacturas) return false
    if (filters.soloMultiplesNotasCredito && !pedido.multiplesNotasCredito) return false

    return true
  })
}

export function emptyOfimaPedidosClientFilters(): OfimaPedidosClientFilters {
  return {
    tiposDocumento: [],
    nit: '',
    fechaDesde: '',
    fechaHasta: '',
    soloMultiplesRemisiones: false,
    soloMultiplesFacturas: false,
    soloMultiplesNotasCredito: false
  }
}
